import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  Stack, Box, CircularProgress, Typography, Divider, Autocomplete,
  Grid, Card, CardActionArea, Chip, Paper
} from "@mui/material";
import {
  DirectionsCar as CarIcon,
  Badge as BadgeIcon, Description as DocIcon, Attachment as AttachmentIcon,
  UploadFile as UploadFileIcon
} from "@mui/icons-material";

import { useDocumentsType } from "../../hooks/useDocumentsType";
import { useVehicles } from "../../hooks/useVehicles";
import { useEmployees } from "../../hooks/useEmployees";

const schema = yup.object({
  doctipid: yup.number().required("El tipo es obligatorio"),
  docfecemi: yup.date().typeError("Fecha inválida").required("Obligatorio"),
  docfecven: yup.date().typeError("Fecha inválida").required("Obligatorio"),
  associationType: yup.string().required("Seleccione una asociación"),
  vehid: yup.number().nullable().when("associationType", {
    is: "vehiculo", then: (s) => s.required("Seleccione un vehículo")
  }),
  empid: yup.number().nullable().when("associationType", {
    is: "empleado", then: (s) => s.required("Seleccione un empleado")
  }),
});

const DocumentsForm = ({ open, handleClose, onSubmit, defaultValues = {}, config = {} }) => {
  const isEditing = Boolean(defaultValues?.docid);
  const [file, setFile] = useState(null);
  const {
    forcedAssociationType = null,
    lockAssociationType = false,
    lockDocumentType = false,
    lockVehicle = false,
    lockEmployee = false,
  } = config;

  const { documentsType = [], isLoading: loadingDocs } = useDocumentsType();
  const { vehicles = [], isLoading: loadingVehs } = useVehicles();
  const { employees = [], isLoading: loadingEmps } = useEmployees();

  const { control, register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { associationType: "vehiculo", vehid: null, empid: null }
  });

  // Observamos los IDs actuales en el formulario
  const currentEmpId = useWatch({ control, name: "empid" });
  const currentVehId = useWatch({ control, name: "vehid" });
  const currentDocTipId = useWatch({ control, name: "doctipid" });
  const associationType = useWatch({ control, name: "associationType" });

  // 1. Efecto para resetear el formulario al abrir
  useEffect(() => {
    if (open) {
      //const assoc = defaultValues.empid ? "empleado" : "vehiculo";
      const assoc = forcedAssociationType || (defaultValues.empid ? "empleado" : "vehiculo");

      reset({
        ...defaultValues,
        associationType: assoc,
        doctipid: defaultValues.doctipid ? Number(defaultValues.doctipid) : "",
        empid: defaultValues.empid ? Number(defaultValues.empid) : null,
        vehid: defaultValues.vehid ? Number(defaultValues.vehid) : null,
        docfecemi: defaultValues.docfecemi ? defaultValues.docfecemi.split('T')[0] : "",
        docfecven: defaultValues.docfecven ? defaultValues.docfecven.split('T')[0] : "",
      });
      setFile(null);
    }
  }, [open, defaultValues, reset, forcedAssociationType]);

  // 2. Memos para encontrar el OBJETO completo que Autocomplete necesita
  // Usamos Number() para asegurar que "8" (string) coincida con 8 (number)
  const selectedDocType = useMemo(() =>
    documentsType.find(t => Number(t.doctipid) === Number(currentDocTipId)) || null
    , [documentsType, currentDocTipId]);

  const selectedEmployee = useMemo(() =>
    employees.find(e => Number(e.empid) === Number(currentEmpId)) || null
    , [employees, currentEmpId]);

  const selectedVehicle = useMemo(() =>
    vehicles.find(v => Number(v.vehid) === Number(currentVehId)) || null
    , [vehicles, currentVehId]);

  const submitFinal = (data) => {
    const finalAssociationType = forcedAssociationType || data.associationType;

    const payload = {
      ...data,
      associationType: finalAssociationType,
      vehid: finalAssociationType === "vehiculo" ? data.vehid : null,
      empid: finalAssociationType === "empleado" ? data.empid : null,
      doctipid: lockDocumentType
        ? Number(defaultValues.doctipid)
        : data.doctipid,
    };

    const formData = new FormData();
    /*const payload = {
      ...data,
      vehid: data.associationType === "vehiculo" ? data.vehid : null,
      empid: data.associationType === "empleado" ? data.empid : null
    };*/
    Object.entries(payload).forEach(([k, v]) => formData.append(k, v ?? ""));
    if (file) formData.append("document", file);
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? "Editar Documento" : "Nuevo Documento"}
      </DialogTitle>

      <form onSubmit={handleSubmit(submitFinal)}>
        <DialogContent dividers>
          <Stack spacing={2.5}>

            {/* Tipo de Documento */}
            {lockDocumentType ? (
              <TextField
                label="Tipo de Documento"
                fullWidth
                value={selectedDocType?.doctip || ""}
                disabled
              />
            ) : (
              <Controller
                name="doctipid"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={documentsType}
                    value={selectedDocType}
                    isOptionEqualToValue={(opt, val) => Number(opt.doctipid) === Number(val?.doctipid)}
                    getOptionLabel={(opt) => opt.doctip || ""}
                    onChange={(_, val) => field.onChange(val?.doctipid ?? "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de Documento"
                        error={!!errors.doctipid}
                        helperText={errors.doctipid?.message}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <DocIcon color="action" sx={{ mr: 1 }} />
                        }}
                      />
                    )}
                  />
                )}
              />
            )}

            <Divider />

            {/* Selector de Asociación */}
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: (theme) => theme.palette.grey[50],
              }}
            >
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Asociación del documento
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Define si este documento pertenece a un vehículo o a un empleado.
                    </Typography>
                  </Box>

                  {lockAssociationType && (
                    <Chip
                      label="Asociación bloqueada"
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                {lockAssociationType ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      bgcolor: associationType === "empleado" ? "warning.50" : "primary.50",
                      borderColor: associationType === "empleado" ? "warning.main" : "primary.main",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: associationType === "empleado" ? "warning.main" : "primary.main",
                        color: "#fff",
                      }}
                    >
                      {associationType === "empleado" ? <BadgeIcon /> : <CarIcon />}
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tipo de asociación
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {associationType === "empleado" ? "Empleado" : "Vehículo"}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {[
                      {
                        id: "vehiculo",
                        label: "Vehículo",
                        description: "Asocia el documento a una unidad vehicular",
                        Icon: CarIcon,
                      },
                      {
                        id: "empleado",
                        label: "Empleado",
                        description: "Asocia el documento a un colaborador",
                        Icon: BadgeIcon,
                      },
                    ].map((item) => {
                      const selected = associationType === item.id;

                      return (
                        <Grid item xs={12} sm={6} key={item.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: "100%",
                              borderRadius: 3,
                              transition: "all 0.2s ease",
                              borderColor: selected ? "primary.main" : "divider",
                              bgcolor: selected ? "primary.50" : "#fff",
                              boxShadow: selected ? 3 : 0,
                            }}
                          >
                            <CardActionArea
                              sx={{ p: 2 }}
                              onClick={() => {
                                setValue("associationType", item.id);
                                setValue(item.id === "vehiculo" ? "empid" : "vehid", null);
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: selected ? "primary.main" : "grey.200",
                                    color: selected ? "#fff" : "text.secondary",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <item.Icon />
                                </Box>

                                <Box flex={1}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {item.label}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {item.description}
                                  </Typography>
                                </Box>

                                {selected && (
                                  <Chip
                                    label="Seleccionado"
                                    color="primary"
                                    size="small"
                                  />
                                )}
                              </Stack>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}

                <Box>
                  {associationType === "empleado" ? (
                    <Controller
                      name="empid"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={employees}
                          loading={loadingEmps}
                          value={selectedEmployee}
                          disabled={lockEmployee}
                          isOptionEqualToValue={(opt, val) => Number(opt.empid) === Number(val?.empid)}
                          getOptionLabel={(opt) => `${opt.pernom || ""} ${opt.perape || ""}`.trim()}
                          onChange={(_, val) => field.onChange(val?.empid ?? null)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Empleado asociado"
                              placeholder="Buscar empleado..."
                              error={!!errors.empid}
                              helperText={errors.empid?.message}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingEmps ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name="vehid"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={vehicles}
                          loading={loadingVehs}
                          value={selectedVehicle}
                          disabled={lockVehicle}
                          isOptionEqualToValue={(opt, val) => Number(opt.vehid) === Number(val?.vehid)}
                          getOptionLabel={(opt) => opt.vehmat || ""}
                          onChange={(_, val) => field.onChange(val?.vehid ?? null)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Vehículo asociado"
                              placeholder="Buscar placa o vehículo..."
                              error={!!errors.vehid}
                              helperText={errors.vehid?.message}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingVehs ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  )}
                </Box>
              </Stack>
            </Box>

            {/* Fechas */}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Emisión" type="date" fullWidth InputLabelProps={{ shrink: true }} {...register("docfecemi")} error={!!errors.docfecemi} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Vencimiento" type="date" fullWidth InputLabelProps={{ shrink: true }} {...register("docfecven")} error={!!errors.docfecven} />
                </Grid>
              </Grid>
            </Box>

            {/* Subida de Archivo */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Archivo del Documento</Typography>

              <Stack spacing={1}>
                {/* Mostrar si ya existe un documento en el servidor */}
                {isEditing && defaultValues.docrut && !file && (
                  <Chip
                    icon={<AttachmentIcon />}
                    label={`Existente: ${defaultValues.docrut.split('-').pop()}`}
                    color="primary"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start', mb: 1 }}
                  />
                )}

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  {file ? "Cambiar Archivo Seleccionado" : "Subir Nuevo Archivo"}
                  <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} />
                </Button>

                {file && (
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                    Seleccionado para subir: {file.name}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" size="large" disableElevation disabled={isSubmitting}>
            {isEditing ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DocumentsForm;